/*
|-------------------------------------------------------------------------------
| VUEX modules/bookings.js
|-------------------------------------------------------------------------------
| The Vuex data store for the cafes

status = 0 -> data hasn't been loaded
status = 1 -> data is loading
status = 2 -> data is loaded successfully
status = 3 -> data is loaded unsuccessfully
*/

import BookingAPI from "../api/booking.js";
import BookingWatchlistAPI from "../api/bookingWatchlist.js";
import NzbnAPI from "../api/nzbn.js";
import SettingAPI from "../api/setting.js";
import axios from 'axios';

export const bookings = {
  state: {
    bookings: [],
    paginateBookings: [],
    bookingsLoadStatus: 0,
    paginateBookingsLoadStatus: 0,
    booking: {},
    bookingLoadStatus: 0,
    nzbn: {},
    nzbnLoadStatus: 0,
    defaultPickupLocation: {},
    defaultPickupLocationLoadStatus: 0,
    pagination: {
      pageSizeOptions: ["10", "20", "30", "50", "100", "200"],
      hideOnSinglePage: true,
      current: 1,
      pageSize: 30,
      total: 100,
      showSizeChanger: true,
      showSizeChange: (current, pageSize) => {
        this.pagination.pageSize = pageSize;
      },
      // defaultPageSize: 20
    },
  },

  actions: {
    loadDefaultPickupLocation({commit}) {
      commit("setDefaultPickupLocationLoadStatus", 1);

      SettingAPI.getDefaultPickupLocation()
        .then(function (response) {
          commit("setDefaultPickupLocation", response.data.data);
          commit("setDefaultPickupLocationLoadStatus", 2);
        })
        .catch(function () {
          commit("setDefaultPickupLocation", {});
          commit("setDefaultPickupLocationLoadStatus", 3);
        });
    },
    loadNzbn({commit}) {
      commit("setNzbnLoadStatus", 1);

      NzbnAPI.getNzbn()
        .then(function (response) {
          commit("setNzbn", response.data);
          commit("setNzbnLoadStatus", 2);
        })
        .catch(function () {
          commit("setNzbn", {});
          commit("setNzbnLoadStatus", 3);
        });
    },
    async loadBookings({commit}) {
      commit("setBookingsLoadStatus", 1);

      await BookingAPI.getBookings({source: "dashboard"})
        .then(function (response) {
          commit("setBookings", response.data.data);
          commit("setBookingsLoadStatus", 2);
        })
        .catch(function () {
          commit("setBookings", []);
          commit("setBookingsLoadStatus", 3);
        });
    },
    loadCarrierBookings({commit, state}, params) {
      commit("setBookingsLoadStatus", 1);
      commit("setPaginateBookingsLoadStatus", 1);
      BookingAPI.getCarrierBookings(params)
        .then(function (response) {
          if (response.config.params.page) {
            commit("setPaginateBookings", response.data.data);
            const pagination = {...state.pagination};
            // Update pagination
            pagination.total = response.headers.maxpages;
            commit("setPagination", pagination);
            commit("setPaginateBookingsLoadStatus", 2);
          } else {
            commit("setBookings", response.data.data);
          }
          commit("setBookingsLoadStatus", 2);
        })
        .catch(function () {
          // commit( 'setBookings', [] );
          // commit( 'setBookingsLoadStatus', 3 );
        });
    },
    updatePagination({commit}, pagination) {
      commit("setPagination", pagination);
    },
    loadBooking({commit}, data) {
      commit("setBookingsLoadStatus", 1);

      BookingAPI.getBooking(data.id)
        .then(function (response) {
          commit("setBookings", response.data.data);
          commit("setBookingsLoadStatus", 2);
        })
        .catch(function () {
          commit("setBookings", {});
          commit("setBookingsLoadStatus", 3);
        });
    },
    addBookingWatchlist({commit}, {booking, params}) {
      const loadingWatchlist = {id: 0};
      const postPromise = BookingWatchlistAPI.postNewBookingWatchlist(booking.id, params);
      commit("updateBookingWatchlist", {
        bookingId: booking.id,
        watchlist: loadingWatchlist,
      });
      postPromise
        .then((response) => {
          commit("updateBookingWatchlist", {
            bookingId: booking.id,
            watchlist: response.data,
          });
        })
        .catch((error) => {
          commit("updateBookingWatchlist", {
            bookingId: booking.id,
            watchlist: null,
          });
          console.log("deleteBookingWatchlist Error: ", error);
        });
    },
    deleteBookingWatchlist({commit}, booking) {
      const oldWatchlist = booking.watchlist;
      const loadingWatchlist = {id: 0};
      const deletePromise = BookingWatchlistAPI.deleteBookingWatchlist(
        booking.id,
        booking.watchlist.id
      );
      commit("updateBookingWatchlist", {
        bookingId: booking.id,
        watchlist: loadingWatchlist,
      });
      deletePromise
        .then(() => {
          commit("updateBookingWatchlist", {
            bookingId: booking.id,
            watchlist: null,
          });
        })
        .catch((error) => {
          commit("updateBookingWatchlist", {
            bookingId: booking.id,
            watchlist: oldWatchlist,
          });
          console.log("deleteBookingWatchlist Error: ", error);
        });
    },
  },

  mutations: {
    setBookingsLoadStatus(state, status) {
      state.bookingsLoadStatus = status;
    },
    setPaginateBookingsLoadStatus(state, status) {
      state.paginateBookingsLoadStatus = status;
    },
    setBookings(state, bookings) {
      state.bookings = bookings.map((res) => mapToBooking(res));
    },
    setPaginateBookings(state, bookings) {
      state.paginateBookings = bookings.map((res) => mapToBooking(res));
    },
    setBookingLoadStatus(state, status) {
      state.bookingLoadStatus = status;
    },
    setBooking(state, booking) {
      state.booking = booking;
    },
    updateBookingWatchlist(state, params) {
      const bookingId = params.bookingId;
      const bookingWatchlist = params.watchlist;

      const newBookings = [...state.bookings];
      const target = newBookings.filter((newBooking) => bookingId === newBooking.id)[0];
      if (target) {
        target.watchlist = bookingWatchlist;
        state.bookings = newBookings;
      }

      const newPageBookings = [...state.paginateBookings];
      const pageTarget = newPageBookings.filter(
        (newPageBooking) => bookingId === newPageBooking.id
      )[0];

      if (pageTarget) {
        pageTarget.watchlist = bookingWatchlist;
        state.bookings = newPageBookings;
      }
    },
    setNzbn(state, nzbn) {
      state.nzbn = nzbn;
    },
    setNzbnLoadStatus(state, status) {
      state.nzbnLoadStatus = status;
    },
    setDefaultPickupLocation(state, defaultPickupLocation) {
      state.defaultPickupLocation = defaultPickupLocation;
    },
    setDefaultPickupLocationLoadStatus(state, status) {
      state.defaultPickupLocationLoadStatus = status;
    },
    setPagination(state, pagination) {
      state.pagination = pagination;
    },
  },

  getters: {
    getBookingsLoadStatus(state) {
      return state.bookingsLoadStatus;
    },
    getBookings(state) {
      return state.bookings;
    },
    getPaginateBookings(state) {
      return state.paginateBookings;
    },
    getWatchlistBookings(state) {
      const watchlistBookings = state.bookings.filter(
        (booking) => booking.watchlist && booking.watchlist.id > 0
      );

      return watchlistBookings;
    },
    getPickupTodayBookings(state) {
      return state.bookings.filter(
        (booking) =>
          booking.status == "Booked" &&
          moment()
            .endOf("day")
            .diff(booking.pickupAfter.endOf("day"), "days") >= 0
      );
    },
    getOverdueBookings(state) {
      return state.bookings.filter(
        (booking) =>
          booking.status !== "Delivered" &&
          moment()
            .endOf("day")
            .diff(booking.deliverBy.endOf("day"), "days") > 0
      );
    },
    getWatchlistOutgoingBookings(state) {
      const watchlistBookings = state.bookings.filter(
        (booking) => booking.watchlist && booking.watchlist.id > 0
      );

      if (state.defaultPickupLocation !== undefined) {
        return watchlistBookings.filter(
          (booking) =>
            state.defaultPickupLocation &&
            !isIncomingBooking(booking.deliveryLocation, booking.ownedLocationIds)
        );
      }
      return [];
    },
    getOutgoingBookings(state) {
      if (state.defaultPickupLocation) {
        return state.bookings.filter(
          (booking) => !isIncomingBooking(booking.deliveryLocation, booking.ownedLocationIds)
        );
      }
      return state.bookings;
    },
    getWatchlistIncomingBookings(state) {
      const watchlistBookings = state.bookings.filter(
        (booking) => booking.watchlist && booking.watchlist.id > 0
      );

      if (state.defaultPickupLocation !== undefined) {
        return watchlistBookings.filter(
          (booking) => isIncomingBooking(booking.deliveryLocation, booking.ownedLocationIds)
        );
      }

      return watchlistBookings;
    },
    getIncomingBookings(state) {
      if (state.defaultPickupLocation) {
        return state.bookings.filter(
          (booking) => isIncomingBooking(booking.deliveryLocation, booking.ownedLocationIds)
        );
      }
      return [];
    },
    getBookingLoadStatus(state) {
      return state.bookingLoadStatus;
    },
    getPaginateBookingsLoadStatus(state) {
      return state.paginateBookingsLoadStatus;
    },
    getBooking(state) {
      return state.booking;
    },
    getNzbn(state) {
      return state.nzbn;
    },
    getNzbnLoadStatus(state) {
      return state.nzbnLoadStatus;
    },
    getDefaultPickupLocation(state) {
      return state.defaultPickupLocation;
    },
    getDefaultPickupLocationLoadStatus(state) {
      return state.defaultPickupLocationLoadStatus;
    },
    getPagination(state) {
      return state.pagination;
    },
    getPaginationTotal(state) {
      return state.pagination.total;
    },
  },
};

/**
 * Helpers
 */

function mapToBooking(res) {
  let booking = {
    id: res.id,
    number: res.number,
    pickupLocation: res.pickup_location,
    pickupRequiresTailLift: res.pickup_requires_taillift,
    deliveryLocation: res.delivery_location,
    deliveryRequiresTailLift: res.delivery_requires_taillift,
    status: res.status,
    latestFreightEvent: {
      source: res.latest_freight_event ? res.latest_freight_event.source : "",
      time: res.latest_freight_event ? res.latest_freight_event.time : "",
      type: res.latest_freight_event ? res.latest_freight_event.type : "",
    },
    status_id: res.status_id,
    eta: moment(res.eta),
    deliverByLocal: moment
      .utc(res.deliver_by)
      .local()
      .format("ddd Do MMM h:mm a"),
    deliverBy: moment(res.deliver_by),
    pickupAfter: moment(res.pickup_after),
    pickupAfterLocal: moment(res.pickup_after)
      .local()
      .format("ddd Do MMM ha"),
    deliveryTime: res.delivery_time ? moment(res.delivery_time) : null,
    hasPod: res.has_pod,
    carrierName: res.carrier_name,
    isNetwork: res.is_network,
    latestAllocatedTruckPlate: res.latest_allocated_truck_plate,
    freightEvents: res.freight_events,
    watchlist: res.watchlist ? res.watchlist : null,
    lines: res.lines.map((line) => mapToLine(line)),
    scanned: res.scanned,
    weight: res.weight,
    volume: res.volume,
    trackerId: res.tracker_id,
    pickupStop: res.pickup_stop,
    deliveryStop: res.delivery_stop,
    tracker: res.tracker,
    trackerStatusBand: res.tracker_status_band,
    ownedLocationIds: res.owned_location_ids
  };
  booking = {...booking, timing: getTimingNumber(booking)};
  return booking;
}

function mapToLine(data) {
  return {
    key: data.id,
    id: data.id,
    description: data.description,
    goodsType: data.goods_type,
    packageType: data.package_type,
    quantity: data.quantity,
    volume: data.volume,
    weight: data.weight,
  };
}

function getTimingNumber(booking) {
  if (booking.deliveryTime !== null && booking.status === "Delivered") {
    return booking.deliveryTime.endOf("day").businessDiff(booking.deliverBy.endOf("day"));
  }
  const etaOrNow = booking.eta.unix() > moment().unix() ? booking.eta : moment();
  return etaOrNow.endOf("day").businessDiff(booking.deliverBy.endOf("day"));
}

function isIncomingBooking(deliveryLocation, ownedLocationIds) {
  return ownedLocationIds.includes(deliveryLocation.id);
}

export const MAP_MOVEMENTS_USER = 'MAP_MOVEMENTS_USER';

export async function getMapMovementsData(bookingId) {
  try {
    let res = await axios.get(`/send-freight/bookings/${bookingId}?t=` + Date.now());
    let booking = res.data.data;let mapData = {
      pickup: {
        lat: null,
        lng: null
      },
      delivery: {
        lat: null,
        lng: null
      },
      latestPoint: {
        lng: null,
        lat: null
      }
    };
    mapData.pickup.lat = Number.parseFloat(booking.pickupLocation.data.address.data.latitude);
    mapData.pickup.lng = Number.parseFloat(booking.pickupLocation.data.address.data.longitude);
    mapData.delivery.lat = Number.parseFloat(booking.deliveryLocation.data.address.data.latitude);
    mapData.delivery.lng = Number.parseFloat(booking.deliveryLocation.data.address.data.longitude);

    if (booking.latestEroadLocation && booking.latestEroadLocation.data.last_location) {
      mapData.latestPoint.lng = Number.parseFloat(booking.latestEroadLocation.data.last_location.lng);
      mapData.latestPoint.lat = Number.parseFloat(booking.latestEroadLocation.data.last_location.lat);
    } else {
      mapData.latestPoint.lng = null;
      mapData.latestPoint.lat = null;
    }
    return mapData;

  } catch (error) {
    console.error(error);
    return null;
  }
}
